import { ELEVATION_NOISE_THRESHOLD_METERS, haversine, SMOOTHING_WINDOW_POINTS } from "./utils.js";

export function buildRouteProfile(sourcePoints) {
  const points = normalizePoints(sourcePoints);
  let minElevation = Infinity;
  let maxElevation = -Infinity;
  let totalDistance = 0;

  points.forEach((point, index) => {
    if (index > 0) {
      totalDistance += haversine(points[index - 1], point);
    }

    point.distance = totalDistance;
    minElevation = Math.min(minElevation, point.elevation);
    maxElevation = Math.max(maxElevation, point.elevation);
  });

  if (points.length < 2) {
    return emptyProfile();
  }

  smoothElevations(points);
  const elevationChange = calculateElevationChange(points);

  return {
    points,
    totalDistance,
    totalAscent: elevationChange.totalAscent,
    totalDescent: elevationChange.totalDescent,
    minElevation,
    maxElevation,
    kmMarkers: buildKmMarkers(points, totalDistance),
    kmSplits: buildKmSplits(points, totalDistance),
  };
}

function emptyProfile() {
  return {
    points: [],
    totalDistance: 0,
    totalAscent: 0,
    totalDescent: 0,
    minElevation: 0,
    maxElevation: 0,
    kmMarkers: [],
    kmSplits: [],
  };
}

function normalizePoints(points) {
  const valid = (points || [])
    .filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude))
    .map((point) => ({
      latitude: point.latitude,
      longitude: point.longitude,
      elevation: Number.isFinite(point.elevation) ? point.elevation : null,
      distance: 0,
      smoothedElevation: Number.isFinite(point.elevation) ? point.elevation : null,
    }));

  fillMissingElevations(valid);
  return valid;
}

function fillMissingElevations(points) {
  points.forEach((point, index) => {
    if (Number.isFinite(point.elevation)) {
      return;
    }

    const previous = findElevation(points, index, -1);
    const next = findElevation(points, index, 1);
    if (Number.isFinite(previous) && Number.isFinite(next)) {
      point.elevation = (previous + next) / 2;
    } else if (Number.isFinite(previous)) {
      point.elevation = previous;
    } else if (Number.isFinite(next)) {
      point.elevation = next;
    } else {
      point.elevation = 0;
    }

    point.smoothedElevation = point.elevation;
  });
}

function findElevation(points, startIndex, direction) {
  for (let index = startIndex + direction; index >= 0 && index < points.length; index += direction) {
    if (Number.isFinite(points[index].elevation)) {
      return points[index].elevation;
    }
  }

  return null;
}

function smoothElevations(points) {
  points.forEach((point, index) => {
    if (points.length < SMOOTHING_WINDOW_POINTS || index < 2 || index > points.length - 3) {
      point.smoothedElevation = point.elevation;
      return;
    }

    const previousTwo = points[index - 2].elevation;
    const previousOne = points[index - 1].elevation;
    const current = point.elevation;
    const nextOne = points[index + 1].elevation;
    const nextTwo = points[index + 2].elevation;

    point.smoothedElevation = (-3 * previousTwo + 12 * previousOne + 17 * current + 12 * nextOne - 3 * nextTwo) / 35;
  });
}

function calculateElevationChange(points) {
  const threshold = ELEVATION_NOISE_THRESHOLD_METERS;
  let totalAscent = 0;
  let totalDescent = 0;
  let trend = 0;
  let anchorElevation = points[0].smoothedElevation;
  let extremeElevation = anchorElevation;

  points.slice(1).forEach((point) => {
    const elevation = point.smoothedElevation;

    if (trend === 0) {
      const diff = elevation - anchorElevation;
      if (Math.abs(diff) >= threshold) {
        trend = diff > 0 ? 1 : -1;
        extremeElevation = elevation;
      }
      return;
    }

    if (trend > 0) {
      if (elevation > extremeElevation) {
        extremeElevation = elevation;
        return;
      }

      if (extremeElevation - elevation >= threshold) {
        totalAscent += extremeElevation - anchorElevation;
        trend = -1;
        anchorElevation = extremeElevation;
        extremeElevation = elevation;
      }
      return;
    }

    if (elevation < extremeElevation) {
      extremeElevation = elevation;
      return;
    }

    if (elevation - extremeElevation >= threshold) {
      totalDescent += anchorElevation - extremeElevation;
      trend = 1;
      anchorElevation = extremeElevation;
      extremeElevation = elevation;
    }
  });

  if (trend > 0 && extremeElevation - anchorElevation >= threshold) {
    totalAscent += extremeElevation - anchorElevation;
  }

  if (trend < 0 && anchorElevation - extremeElevation >= threshold) {
    totalDescent += anchorElevation - extremeElevation;
  }

  return { totalAscent, totalDescent };
}

function buildKmMarkers(points, totalDistance) {
  const markers = [];
  const maxKm = Math.floor(totalDistance);

  for (let km = 1; km <= maxKm; km += 1) {
    markers.push({
      km,
      distance: km,
      elevation: interpolatePoint(points, km).smoothedElevation,
    });
  }

  return markers;
}

function buildKmSplits(points, totalDistance) {
  const splits = [];
  const splitCount = Math.ceil(totalDistance);

  for (let index = 0; index < splitCount; index += 1) {
    const startDistance = index;
    const endDistance = Math.min(index + 1, totalDistance);
    if (endDistance - startDistance <= 0.001) {
      continue;
    }

    const elevationChange = calculateElevationChange(slicePointsByDistance(points, startDistance, endDistance));
    splits.push({
      index: index + 1,
      startDistance,
      endDistance,
      totalAscent: elevationChange.totalAscent,
      totalDescent: elevationChange.totalDescent,
    });
  }

  return splits;
}

function slicePointsByDistance(points, startDistance, endDistance) {
  const segmentPoints = [interpolatePoint(points, startDistance)];

  points.forEach((point) => {
    if (point.distance > startDistance && point.distance < endDistance) {
      segmentPoints.push(Object.assign({}, point));
    }
  });

  segmentPoints.push(interpolatePoint(points, endDistance));
  return segmentPoints;
}

function interpolatePoint(points, distance) {
  if (distance <= points[0].distance) {
    return Object.assign({}, points[0], { distance });
  }

  const lastPoint = points[points.length - 1];
  if (distance >= lastPoint.distance) {
    return Object.assign({}, lastPoint, { distance });
  }

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (current.distance >= distance) {
      const span = current.distance - previous.distance;
      const ratio = span > 0 ? (distance - previous.distance) / span : 0;
      return {
        latitude: previous.latitude + (current.latitude - previous.latitude) * ratio,
        longitude: previous.longitude + (current.longitude - previous.longitude) * ratio,
        elevation: previous.elevation + (current.elevation - previous.elevation) * ratio,
        smoothedElevation: previous.smoothedElevation + (current.smoothedElevation - previous.smoothedElevation) * ratio,
        distance,
      };
    }
  }

  return Object.assign({}, lastPoint, { distance });
}
